import React, { useEffect, useState, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import AfitdNumberFormat from './AfitdNumberFormat';
import AfitdTypography from './AfitdTypography';
import AfitdTextField from './AfitdTextField';
import { DEFAULT_CURRENCY_SYMBOL } from '../constants';
import { getIsExactRatePlaceholder, getRateMinPlaceholder, getRateMaxPlaceholder } from '../placeholderHelper';

import _isNil from 'lodash/isNil';
// import _toNumber from 'lodash/toNumber';

const styles = theme => ({});

const RADIO_EXACT = 'exact';
const RADIO_RANGE = 'range';
const ERROR_INVALID_RANGE = 'Invalid Range';
// const ERROR_INVALID_SALARY_RATE_RANGE = 'Invalid Salary/Rate Range';

// build object. Set rateMax to rateMin if exact
const getPayload = (isExactRate, rateMin, rateMax) => ({
    isExactRate,
    rateMin,
    rateMax: isExactRate ? rateMin : rateMax,
});

const validateRateRange = (payload, setIsInvalidRange, setIsValid) => {
    const { isExactRate, rateMin, rateMax } = payload;

    if (!isExactRate) {
        const isInvalidRange = !_isNil(rateMin) && !_isNil(rateMax) && rateMin > rateMax;
        setIsInvalidRange(isInvalidRange); // set local state
        setIsValid && setIsValid(!isInvalidRange); // tell the parent about this error
    }
};

const AfitdSalaryRate = props => {
    const { isHidden, isExactRate, rateMin, rateMax, rateMinError, rateMaxError, onChange, setIsValid, currencySymbol /*, ...otherProps*/ } = props;
    const [isExactRateLocal, setIsExactRateLocal] = useState(isExactRate ? RADIO_EXACT : RADIO_RANGE);
    const [isInvalidRange, setIsInvalidRange] = useState(false);

    useEffect(() => {
        setIsExactRateLocal(isExactRate ? RADIO_EXACT : RADIO_RANGE);
    }, [isExactRate]);

    const handleChangeIsExactRate = useCallback(
        evt => {
            const value = evt.target.value;
            const isExactChecked = value === RADIO_EXACT;
            const payload = getPayload(isExactChecked, rateMin, rateMax);
            setIsExactRateLocal(value);
            if (isExactChecked) {
                setIsInvalidRange(false); // set local state
                setIsValid && setIsValid(true); // tell the parent to clear the error, if any
            }
            onChange && onChange(payload, 'isExactRate', isInvalidRange);
        },
        [rateMin, rateMax, setIsValid, onChange, isInvalidRange],
    );

    const handleChangeRateMin = useCallback(
        value => {
            const rateMinValue = value.floatValue;
            const isExactChecked = isExactRateLocal === RADIO_EXACT;
            const payload = getPayload(isExactChecked, rateMinValue, rateMax);
            validateRateRange(payload, setIsInvalidRange, setIsValid);
            onChange && onChange(payload, 'rateMin', isInvalidRange);
        },
        [isExactRateLocal, rateMax, setIsValid, onChange, isInvalidRange],
    );

    const handleChangeRateMax = useCallback(
        value => {
            const rateMaxValue = value.floatValue;
            const isExactChecked = isExactRateLocal === RADIO_EXACT;
            const payload = getPayload(isExactChecked, rateMin, rateMaxValue);
            validateRateRange(payload, setIsInvalidRange, setIsValid);
            onChange && onChange(payload, 'rateMax', isInvalidRange);
        },
        [isExactRateLocal, rateMin, setIsValid, onChange, isInvalidRange],
    );

    const getRateMinMaxHelperText = useCallback(() => {
        return isInvalidRange ? ERROR_INVALID_RANGE : '';
    }, [isInvalidRange]);

    const rateMaxIsHidden = isExactRateLocal === RADIO_EXACT;
    const rateMinPlaceholder = isExactRateLocal === RADIO_EXACT ? getIsExactRatePlaceholder(currencySymbol) : getRateMinPlaceholder(currencySymbol);
    const rateMaxPlaceholder = getRateMaxPlaceholder(currencySymbol);

    if (isHidden) {
        return null;
    } else {
        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <RadioGroup row value={isExactRateLocal} onChange={handleChangeIsExactRate}>
                        <FormControlLabel control={<Radio />} value={RADIO_EXACT} label="Exact" />
                        <FormControlLabel control={<Radio />} value={RADIO_RANGE} label="Range" />
                    </RadioGroup>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <AfitdNumberFormat
                        placeholder={rateMinPlaceholder}
                        value={rateMin}
                        customInput={AfitdTextField}
                        thousandSeparator
                        onValueChange={handleChangeRateMin}
                        error={isInvalidRange || rateMinError}
                        helperText={getRateMinMaxHelperText()}
                        prefix={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                    />
                    <AfitdTypography isHidden={rateMaxIsHidden} style={{ margin: '16px 10px 0 10px' }} value="to" />
                    <AfitdNumberFormat
                        placeholder={rateMaxPlaceholder}
                        value={rateMax}
                        customInput={AfitdTextField}
                        thousandSeparator
                        isHidden={rateMaxIsHidden}
                        onValueChange={handleChangeRateMax}
                        error={isInvalidRange || rateMaxError}
                        helperText={getRateMinMaxHelperText()}
                        prefix={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                    />
                </div>
            </div>
        );
    }
};

export default withStyles(styles)(AfitdSalaryRate);
